/**
 * AI usage contracts and the pure aggregation Settings renders.
 *
 * The ledger exists because the product had no way to answer "which provider
 * actually answered, and did it fail?" — the outage that motivated per-task
 * routing was diagnosed by reading Cloud Functions logs, which the person
 * using Zunelo cannot do. With per-task routing a call can also silently land
 * on a fallback provider, and nothing showed that either.
 *
 * It records counters and identifiers only: never a prompt, a response, a
 * thread, a ticket or a diff.
 */
import { AI_TASK_IDS } from './aiTasks.js';
/** Rollup collection; the retention sweep and the ledger must agree on it. */
export const AI_USAGE_COLLECTION = 'ai_usage';
export const AI_USAGE_TASKS = [
    ...AI_TASK_IDS,
    'connectionProbe',
    'unknown',
];
export function isAiUsageTask(value) {
    return typeof value === 'string' && AI_USAGE_TASKS.includes(value);
}
/** Windows Settings offers; anything else is clamped into this range. */
export const AI_USAGE_WINDOWS = [7, 30, 90];
export function normalizeAiUsageWindow(value) {
    const parsed = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
    const match = AI_USAGE_WINDOWS.find((window) => window === parsed);
    return match ?? 30;
}
/** `YYYY-MM-DD` in UTC — the rollup key and the axis Settings groups by. */
export function aiUsageDayKey(at) {
    const date = typeof at === 'string' ? new Date(at) : at;
    if (Number.isNaN(date.getTime()))
        return 'invalid';
    return date.toISOString().slice(0, 10);
}
/** Day keys for a window, oldest first, so a zero day is still a row. */
export function aiUsageDayKeys(until, days) {
    const end = typeof until === 'string' ? new Date(until) : until;
    const span = Math.max(1, Math.min(366, Math.trunc(days)));
    const keys = [];
    for (let offset = span - 1; offset >= 0; offset -= 1) {
        keys.push(aiUsageDayKey(new Date(end.getTime() - offset * 24 * 3600 * 1000)));
    }
    return keys;
}
function emptyTotals() {
    return {
        calls: 0,
        failures: 0,
        promptTokens: 0,
        completionTokens: 0,
        totalTokens: 0,
        avgLatencyMs: 0,
    };
}
function newAccumulator() {
    return { ...emptyTotals(), latencyMsTotal: 0, lastAt: null, lastErrorAt: null };
}
/**
 * Only a real ISO timestamp may win the "last used" comparison: a malformed
 * value sorts above every valid one lexicographically and would blank the
 * column whose whole job is answering "when".
 */
export function isIsoTimestamp(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T[\d:.]+Z$/.test(value))
        return false;
    return !Number.isNaN(new Date(value).getTime());
}
function accumulate(target, entry) {
    target.calls += entry.calls;
    target.failures += entry.failures;
    target.promptTokens += entry.promptTokens;
    target.completionTokens += entry.completionTokens;
    target.totalTokens += entry.totalTokens;
    target.latencyMsTotal += entry.latencyMsTotal;
    if (!isIsoTimestamp(entry.lastAt))
        return;
    if (!target.lastAt || entry.lastAt > target.lastAt)
        target.lastAt = entry.lastAt;
    if (!entry.failures || entry.lastErrorStatus === undefined)
        return;
    // Ordered by when the failure happened, not by the bucket's last call: a
    // later success in the same bucket must not promote an older failure.
    const failedAt = isIsoTimestamp(entry.lastErrorAt) ? entry.lastErrorAt : entry.lastAt;
    if (!target.lastErrorAt || failedAt > target.lastErrorAt) {
        target.lastErrorAt = failedAt;
        target.lastErrorStatus = entry.lastErrorStatus;
    }
}
function finish(key, label, acc) {
    return {
        key,
        label,
        calls: acc.calls,
        failures: acc.failures,
        promptTokens: acc.promptTokens,
        completionTokens: acc.completionTokens,
        totalTokens: acc.totalTokens,
        avgLatencyMs: acc.calls ? Math.round(acc.latencyMsTotal / acc.calls) : 0,
        lastAt: acc.lastAt,
        ...(acc.lastErrorStatus !== undefined ? { lastErrorStatus: acc.lastErrorStatus } : {}),
    };
}
function groupBy(days, keyOf) {
    const accumulators = new Map();
    for (const day of days) {
        for (const entry of day.entries) {
            const key = keyOf(entry);
            const acc = accumulators.get(key) ?? newAccumulator();
            accumulate(acc, entry);
            accumulators.set(key, acc);
        }
    }
    return ([...accumulators.entries()]
        .map(([key, acc]) => finish(key, key, acc))
        // Busiest first: the question is "who is doing the work", not the alphabet.
        .sort((a, b) => b.calls - a.calls || a.key.localeCompare(b.key)));
}
/**
 * Aggregate stored rollups into what Settings renders. Pure: the API and the
 * SPA agree on the numbers because they run the same function.
 */
export function summarizeAiUsage(days, window) {
    const totals = newAccumulator();
    for (const day of days)
        for (const entry of day.entries)
            accumulate(totals, entry);
    const byDay = days
        .map((day) => {
        const acc = newAccumulator();
        for (const entry of day.entries)
            accumulate(acc, entry);
        return finish(day.day, day.day, acc);
    })
        // Chronological: a usage timeline read backwards is a puzzle.
        .sort((a, b) => a.key.localeCompare(b.key));
    return {
        since: window.since,
        until: window.until,
        totals: finish('totals', 'totals', totals),
        byProvider: groupBy(days, (entry) => entry.provider),
        byModel: groupBy(days, (entry) => `${entry.provider} · ${entry.model}`),
        byTask: groupBy(days, (entry) => entry.task),
        byDay,
        byAttempt: groupBy(days, (entry) => entry.attempt),
    };
}
//# sourceMappingURL=aiUsage.js.map